import { useEffect, useRef, useState } from 'react'
import type { TicketStagesBand } from '../api/easeedesk'
import './TicketStages.css'

/**
 * Drumul cererii, cu stadiul de acum marcat - `easeedesk/ticket/_stages.php`
 * cu `public/js/views/easeedesk/ticket/_stages-1.js`.
 *
 * Numai pentru tichetele deschise din catalogul de servicii: capul trimite
 * `stages: null` pentru celelalte, si atunci nu se deseneaza nimic. Acolo omul
 * a vazut drumul inainte sa ceara, la "What happens next"; daca dupa aceea
 * tichetul ii arata doar un status singur, ecranul de dupa comanda ii spune
 * mai putin decat cel de dinainte.
 *
 * Trei stari, trei culori: verde ce s-a facut, albastru unde suntem, gri ce
 * urmeaza. Cand statusul de acum nu e in flux - cineva poate muta tichetul cu
 * mana intr-un status care nu e pe drum - nu se marcheaza nimic ca trecut. Mai
 * bine un drum nemarcat decat unul marcat gresit.
 */
export function TicketStages({ band }: { band: TicketStagesBand | null }) {
  const [open, setOpen] = useState(true)
  const list = useRef<HTMLDivElement>(null)

  const stages = band?.stages ?? []
  const current = band?.current ?? 0
  const at = stages.findIndex((stage) => stage.status_id === current)

  /*
   * Cutia se deruleaza singura pana cand stadiul de acum ajunge sus (`_stages-1.js`).
   *
   * Fara asta, un flux lung se deschide la primul stadiu - adica taman la ce
   * s-a intamplat demult - si tichetul pare a fi la inceput pana cand cineva
   * deruleaza. `scrollTop` direct, nu scrollIntoView: acela plimba si pagina din
   * spatele cutiei. Cat timp cutia n-are inaltime (fila ascunsa), n-are nici
   * pozitii de citit - se incearca de cateva ori, rar, apoi se lasa.
   */
  useEffect(() => {
    const box = list.current
    if (!box) return
    let tries = 0
    let timer: ReturnType<typeof setTimeout> | undefined

    const place = (): void => {
      const now = box.querySelector<HTMLElement>('[data-now="1"]')
      if (!now) return
      if (!box.clientHeight) {
        if (++tries < 10) timer = setTimeout(place, 200)
        return
      }
      box.scrollTop = now.getBoundingClientRect().top - box.getBoundingClientRect().top + box.scrollTop
    }

    place()

    return () => {
      if (timer !== undefined) clearTimeout(timer)
    }
  }, [band])

  if (band === null || stages.length === 0) return null

  return (
    <div className="tk-stages">
      <h4>
        <i
          className={`fa ${open ? 'fa-angle-up' : 'fa-angle-down'} toggle-details`}
          aria-hidden="true"
          onClick={() => setOpen(!open)}
        ></i>{' '}
        {/* Aceleasi cuvinte ca in catalog, la fisa serviciului. */}
        What happens next {band.name !== '' && <span className="tk-stages-name">{band.name}</span>}
      </h4>
      <hr className="dotted" style={{ marginTop: 5 }} />

      {/* Doua invelisuri, ca in macheta: se pliaza invelisul, lista ramane cum e. */}
      <div className="slide-details" hidden={!open}>
        <div className="tk-stages-list" ref={list}>
          {stages.map((stage, i) => {
            const isNow = at !== -1 && i === at
            const isDone = at !== -1 && i < at
            /* Un stadiu de tip rezolvat sau final inseamna munca terminata: "acum"
               si "gata" sunt acelasi lucru, si se arata verde, nu albastru. */
            const isEnd = isNow && stage.type >= 2

            let className = 'tk-stage'
            if (isNow) className += isEnd ? ' tk-stage-now tk-stage-end' : ' tk-stage-now'
            else if (isDone) className += ' tk-stage-done'

            /* Bulina isi ia culoarea statusului numai pe stadiul curent. */
            const dot = isNow && !isEnd && stage.colour ? stage.colour : null

            return (
              <div key={stage.status_id} className={className} data-now={isNow ? '1' : undefined}>
                {isDone || isEnd ? (
                  <i className="fas fa-check tk-stage-tick" aria-hidden="true"></i>
                ) : (
                  <span className="tk-stage-dot" style={dot ? { background: dot } : undefined}></span>
                )}
                <span className="tk-stage-name">{stage.name}</span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
